import * as React from 'react'
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { getJSON, deleteJSON, patchJSON, postJSON } from '@/lib/api'
import { displayDate } from '@/lib/dates'
import { stripePromise } from '@/lib/stripe'
import { CancelDialog, EditGift, type RecurringGift } from './MemberPortalGivingManageSections'

export function MemberPortalGivingManage() {
  const queryClient = useQueryClient()
  const gift = useQuery<{ recurring_gift: RecurringGift | null }>({
    queryKey: ['member-portal-recurring-gift'],
    queryFn: () => getJSON('/api/v1/member_portal/recurring_gift'),
    retry: false,
  })
  const cancel = useMutation({
    mutationFn: () => deleteJSON('/api/v1/member_portal/recurring_gift'),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['member-portal-recurring-gift'] }),
        queryClient.invalidateQueries({ queryKey: ['member-portal-giving'] }),
        queryClient.invalidateQueries({ queryKey: ['member-portal-giving-availability'] }),
      ])
    },
  })
  const update = useMutation({
    mutationFn: (values: Partial<RecurringGift>) =>
      patchJSON('/api/v1/member_portal/recurring_gift', { recurring_gift: values }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['member-portal-recurring-gift'] })
    },
  })
  const pause = useMutation({
    mutationFn: (paused_until: string) =>
      postJSON('/api/v1/member_portal/recurring_gift/pause', { paused_until }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['member-portal-recurring-gift'] }),
  })
  const resume = useMutation({
    mutationFn: () => postJSON('/api/v1/member_portal/recurring_gift/resume', {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['member-portal-recurring-gift'] }),
  })

  if (gift.isLoading)
    return (
      <p className="text-body-sm text-muted-foreground" role="status">
        Loading recurring gift...
      </p>
    )
  if (gift.isError) return <Alert>We couldn’t load your recurring gift.</Alert>
  const plan = gift.data?.recurring_gift
  if (!plan) return <p className="text-body-sm text-muted-foreground">No recurring gift.</p>

  return (
    <ManageContent
      plan={plan}
      cancel={cancel}
      update={update}
      pause={pause}
      resume={resume}
      onCardSaved={() =>
        queryClient.invalidateQueries({ queryKey: ['member-portal-recurring-gift'] })
      }
    />
  )
}

function ManageContent({
  plan,
  cancel,
  update,
  pause,
  resume,
  onCardSaved,
}: {
  plan: RecurringGift
  cancel: { isError: boolean; isPending: boolean; mutate: () => void }
  update: {
    error: Error | null
    mutate: (values: Partial<RecurringGift>, options?: { onSuccess?: () => void }) => void
  }
  pause: { mutate: (date: string) => void; isError: boolean }
  resume: { mutate: () => void; isPending: boolean }
  onCardSaved: () => void
}) {
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [editing, setEditing] = React.useState<string | null>(null)
  const [draft, setDraft] = React.useState<Partial<RecurringGift>>({})
  const [pauseDate, setPauseDate] = React.useState('')
  const [editingCard, setEditingCard] = React.useState(false)

  const frequency = plan.frequency === 'monthly' ? 'Monthly' : 'Annual'
  const ended = plan.status === 'ended'
  const paused = plan.status === 'paused'
  const cardFailed = plan.status === 'disabled'
  return (
    <section className="mx-auto max-w-xl space-y-6">
      <header className="space-y-1">
        <h1 className="text-subheading font-semibold">Your {frequency.toLowerCase()} gift</h1>
        <p className="text-caption text-muted-foreground">Manage your recurring giving plan.</p>
      </header>
      <GiftFactSheet
        plan={plan}
        frequency={frequency}
        ended={ended}
        paused={paused}
        resume={resume}
        editing={editing}
        draft={draft}
        setDraft={setDraft}
        setEditing={setEditing}
        update={update}
        cardFailed={cardFailed}
        editingCard={editingCard}
        setEditingCard={setEditingCard}
        onCardSaved={onCardSaved}
      />
      {!ended && !paused && !cardFailed && (
        <div className="flex items-center gap-2">
          <Input
            aria-label="Resume date"
            type="date"
            className="h-8 w-40"
            value={pauseDate}
            onChange={(event) => setPauseDate(event.target.value)}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => pause.mutate(pauseDate)}
          >
            Pause
          </Button>
        </div>
      )}
      {pause.isError && <Alert>Choose a future resume date within 12 months.</Alert>}
      {!ended && !cardFailed && (
        <Button
          type="button"
          variant="quietDestructive"
          size="sm"
          className="h-8 border-border bg-transparent"
          onClick={() => setConfirmOpen(true)}
        >
          Cancel gift
        </Button>
      )}
      {!ended && (
        <CancelDialog
          open={confirmOpen}
          setOpen={setConfirmOpen}
          frequency={frequency}
          cancel={cancel}
        />
      )}
    </section>
  )
}

function GiftFactSheet({
  plan,
  frequency,
  ended,
  paused,
  resume,
  editing,
  draft,
  setDraft,
  setEditing,
  update,
  cardFailed,
  editingCard,
  setEditingCard,
  onCardSaved,
}: {
  plan: RecurringGift
  frequency: string
  ended: boolean
  paused: boolean
  resume: { mutate: () => void; isPending: boolean }
  editing: string | null
  draft: Partial<RecurringGift>
  setDraft: React.Dispatch<React.SetStateAction<Partial<RecurringGift>>>
  setEditing: React.Dispatch<React.SetStateAction<string | null>>
  update: {
    error: Error | null
    mutate: (values: Partial<RecurringGift>, options?: { onSuccess?: () => void }) => void
  }
  cardFailed: boolean
  editingCard: boolean
  setEditingCard: React.Dispatch<React.SetStateAction<boolean>>
  onCardSaved: () => void
}) {
  const startEditing = () => {
    setDraft({
      amount: plan.amount.replace(/[^0-9.-]/g, ''),
      frequency: plan.frequency,
      next_charge_on: plan.next_charge_on,
      campaign: plan.campaign,
    })
    setEditing('gift')
  }

  return (
    <article className="overflow-hidden rounded border border-border bg-card">
      {cardFailed && (
        <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted px-4 py-3">
          <Badge variant="warning">Card failed</Badge>
          <p className="min-w-0 flex-1 text-body-sm text-foreground">
            Your gift is stopped until the card is updated.
          </p>
          <Button type="button" size="sm" className="h-8" onClick={() => setEditingCard(true)}>
            Update card
          </Button>
        </div>
      )}
      {paused && <PauseStrip plan={plan} resume={resume} />}
      {ended && (
        <div className="border-b border-border bg-muted px-4 py-3">
          <Badge variant="neutral">Ended</Badge>
        </div>
      )}
      {editing === 'gift' ? (
        <EditGift
          draft={draft}
          setDraft={setDraft}
          onSave={() => update.mutate(draft, { onSuccess: () => setEditing(null) })}
          onCancel={() => setEditing(null)}
          error={update.error}
        />
      ) : (
        <ReadSection
          label="Gift"
          value={`${plan.amount} · ${frequency}`}
          caption={
            ended
              ? 'No more charges'
              : paused
                ? `Resumes ${displayDate(plan.paused_until ?? '')}`
                : `Next charge ${displayDate(plan.next_charge_on)}`
          }
          onEdit={ended ? undefined : startEditing}
        />
      )}
      <ReadSection
        label="Payment method"
        value={`${plan.card.brand} ·· ${plan.card.last4}`}
        caption="Saved payment method"
      />
      {editingCard && (
        <CardUpdate
          onSaved={() => {
            onCardSaved()
            setEditingCard(false)
          }}
          onCancel={() => setEditingCard(false)}
        />
      )}
      <ReadSection
        label="Campaign"
        value={plan.campaign}
        caption="Recurring gifts keep charging archived campaigns"
        onEdit={ended ? undefined : startEditing}
      />
      <ReadSection
        label="Receipts"
        value="Sent after every charge"
        caption="Your giving history and receipts stay available."
      />
    </article>
  )
}

function CardUpdate({ onSaved, onCancel }: { onSaved: () => void; onCancel: () => void }) {
  if (!stripePromise) return <Alert>Saved Card entry is not configured.</Alert>
  return (
    <Elements stripe={stripePromise}>
      <HostedCardUpdate onSaved={onSaved} onCancel={onCancel} />
    </Elements>
  )
}

function HostedCardUpdate({ onSaved, onCancel }: { onSaved: () => void; onCancel: () => void }) {
  const stripe = useStripe()
  const elements = useElements()
  const save = useMutation({
    mutationFn: async () => {
      const card = elements?.getElement(CardElement)
      if (!stripe || !card) throw new Error('Processor-hosted card entry is not ready.')
      const key = crypto.randomUUID()
      const setup = await postJSON<{ card_setup: { id: number; client_secret: string } }>(
        '/api/v1/member_portal/recurring_gift/card_setups',
        { card_setup: { idempotency_key: key } },
      )
      const confirmation = await stripe.confirmCardSetup(setup.card_setup.client_secret, {
        payment_method: { card },
      })
      if (confirmation.error)
        throw new Error(confirmation.error.message ?? 'Saved Card could not be submitted.')
      return postJSON(
        '/api/v1/member_portal/recurring_gift/card_setups/' + setup.card_setup.id + '/complete',
        {},
      )
    },
    onSuccess: onSaved,
  })
  return (
    <div className="space-y-3 border-b border-border px-4 py-4">
      <div className="rounded-md border border-border bg-background px-3 py-3">
        <CardElement options={{ hidePostalCode: false }} />
      </div>
      {save.isError && <Alert variant="destructive">{save.error.message}</Alert>}
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          disabled={!stripe || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saving card...' : 'Update Saved Card'}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

function PauseStrip({
  plan,
  resume,
}: {
  plan: RecurringGift
  resume: { mutate: () => void; isPending: boolean }
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted px-4 py-3">
      <Badge variant="warning">Paused</Badge>
      <span className="mr-auto text-body-sm text-foreground">
        Resumes {displayDate(plan.paused_until ?? '')}
      </span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-8"
        disabled={resume.isPending}
        onClick={() => resume.mutate()}
      >
        Resume now
      </Button>
    </div>
  )
}

function ReadSection({
  label,
  value,
  caption,
  onEdit,
}: {
  label: string
  value: string
  caption: string
  onEdit?: () => void
}) {
  return (
    <section className="border-b border-border px-4 py-4 last:border-b-0">
      <div className="flex items-start gap-3">
        <p className="w-28 shrink-0 pt-0.5 text-caption text-muted-foreground">{label}</p>
        <div className="min-w-0 flex-1">
          <p className="text-body-sm text-foreground">{value}</p>
          <p className="mt-0.5 text-caption text-muted-foreground">{caption}</p>
        </div>
        {onEdit && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="size-8 shrink-0 px-0"
            aria-label={`Edit ${label}`}
            onClick={onEdit}
          >
            ✎
          </Button>
        )}
      </div>
    </section>
  )
}
